import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { formatInboxEmail } from "../utils/inbox";

function UserApiAccessSection({ user, getAuthHeaders, onUserUpdate }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [copyLabel, setCopyLabel] = useState("");
  const copyTimeoutRef = useRef(null);

  const apiBaseUrl = info?.baseUrl || `${window.location.origin}/api/v1`;

  const loadInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/auth/api-access", {
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to load API access info.");
      }
      setInfo(data);
    } catch (err) {
      setIsError(true);
      setMessage(err.message || "Failed to load API access info.");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const showCopyFeedback = (label) => {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    setCopyLabel(label);
    copyTimeoutRef.current = setTimeout(() => {
      setCopyLabel("");
      copyTimeoutRef.current = null;
    }, 2000);
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showCopyFeedback(label);
    } catch {
      setIsError(true);
      setMessage("Could not copy to clipboard.");
    }
  };

  const handleGenerate = async () => {
    setMessage("");
    setIsError(false);
    setGenerating(true);
    setNewApiKey("");

    try {
      const res = await fetch("/api/auth/api-access/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to generate API key.");
      }
      setNewApiKey(data.apiKey);
      setInfo((prev) => ({
        ...prev,
        apiKeyConfigured: true,
        apiKeyPrefix: data.apiKeyPrefix,
        apiKeyCreatedAt: data.apiKeyCreatedAt,
      }));
      if (data.user && onUserUpdate) {
        onUserUpdate(data.user);
      }
      setMessage(data.message);
    } catch (err) {
      setIsError(true);
      setMessage(err.message || "Failed to generate API key.");
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("Revoke your API key? Existing integrations will stop working.")) {
      return;
    }

    setMessage("");
    setIsError(false);
    setRevoking(true);
    setNewApiKey("");

    try {
      const res = await fetch("/api/auth/api-access", {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to revoke API key.");
      }
      setInfo((prev) => ({
        ...prev,
        apiKeyConfigured: false,
        apiKeyPrefix: null,
        apiKeyCreatedAt: null,
      }));
      if (data.user && onUserUpdate) {
        onUserUpdate(data.user);
      }
      setMessage(data.message || "API key revoked.");
    } catch (err) {
      setIsError(true);
      setMessage(err.message || "Failed to revoke API key.");
    } finally {
      setRevoking(false);
    }
  };

  const exampleInbox = user?.activeInbox || "myinbox";
  const exampleAddress = formatInboxEmail(
    exampleInbox,
    user?.inboxSubdomain || info?.inboxSubdomain,
    user?.role === "admin",
  );
  const exampleLocalPart = exampleAddress.split("@")[0] || "myinbox.subdomain";

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-sm text-zinc-500">
        Loading API access…
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">API Access</h2>
        <p className="mt-1 text-sm text-zinc-400">
          Integrate Ghost Inbox into your apps and automation workflows.
        </p>
      </div>

      <div className="space-y-6">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">API base URL</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="flex-1 break-all rounded-lg bg-zinc-950 px-3 py-2 font-mono text-sm text-emerald-400">
              {apiBaseUrl}
            </code>
            <button
              type="button"
              onClick={() => copyText(apiBaseUrl, "url")}
              className="shrink-0 rounded-lg border border-zinc-700 px-3 py-2 text-xs text-zinc-300 hover:border-zinc-500 hover:text-white"
            >
              {copyLabel === "url" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">API key</p>
              {info?.apiKeyConfigured ? (
                <p className="mt-2 font-mono text-sm text-zinc-200">
                  {info.apiKeyPrefix} <span className="text-zinc-500">(hidden)</span>
                </p>
              ) : (
                <p className="mt-2 text-sm text-zinc-400">No API key generated yet.</p>
              )}
              {info?.apiKeyCreatedAt && (
                <p className="mt-1 text-xs text-zinc-500">
                  Created {new Date(info.apiKeyCreatedAt).toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating || revoking}
                className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-60"
              >
                {generating
                  ? "Generating…"
                  : info?.apiKeyConfigured
                    ? "Regenerate key"
                    : "Generate API key"}
              </button>
              {info?.apiKeyConfigured && (
                <button
                  type="button"
                  onClick={handleRevoke}
                  disabled={generating || revoking}
                  className="rounded-lg border border-red-800/60 px-4 py-2 text-sm text-red-300 hover:border-red-600 disabled:opacity-60"
                >
                  {revoking ? "Revoking…" : "Revoke"}
                </button>
              )}
            </div>
          </div>

          {newApiKey && (
            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
              <p className="text-xs font-medium text-amber-300">Your new API key (copy now)</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="flex-1 break-all rounded bg-zinc-950 px-3 py-2 font-mono text-xs text-emerald-400">
                  {newApiKey}
                </code>
                <button
                  type="button"
                  onClick={() => copyText(newApiKey, "key")}
                  className="shrink-0 rounded-lg border border-zinc-700 px-3 py-2 text-xs text-zinc-300 hover:border-zinc-500"
                >
                  {copyLabel === "key" ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
          <h3 className="text-sm font-semibold text-white">Documentation</h3>
          <p className="mt-2 text-sm leading-relaxed text-zinc-400">
            Authenticate every request with your API key using either header:
          </p>
          <pre className="mt-3 overflow-x-auto rounded-lg bg-zinc-950 p-4 font-mono text-xs text-zinc-300">
{`Authorization: Bearer YOUR_API_KEY
# or
X-API-Key: YOUR_API_KEY`}
          </pre>

          <p className="mt-5 text-sm text-zinc-400">
            Your account subdomain is{" "}
            <span className="font-mono text-emerald-400">
              .{user?.inboxSubdomain || info?.inboxSubdomain || "xxxx"}
            </span>
            . Mail sent to addresses like{" "}
            <span className="font-mono text-emerald-400">{exampleAddress}</span> can be read via
            the API.
          </p>

          <div className="mt-5 space-y-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                GET /me — Account info
              </p>
              <pre className="mt-2 overflow-x-auto rounded-lg bg-zinc-950 p-3 font-mono text-xs text-zinc-300">
{`curl -H "Authorization: Bearer YOUR_API_KEY" \\
  ${apiBaseUrl}/me`}
              </pre>
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                GET /inbox — Active inbox messages
              </p>
              <pre className="mt-2 overflow-x-auto rounded-lg bg-zinc-950 p-3 font-mono text-xs text-zinc-300">
{`curl -H "Authorization: Bearer YOUR_API_KEY" \\
  ${apiBaseUrl}/inbox`}
              </pre>
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                GET /inbox/:name — Specific inbox under your subdomain
              </p>
              <pre className="mt-2 overflow-x-auto rounded-lg bg-zinc-950 p-3 font-mono text-xs text-zinc-300">
{`curl -H "Authorization: Bearer YOUR_API_KEY" \\
  ${apiBaseUrl}/inbox/${exampleInbox}

# or by full local part
curl -H "Authorization: Bearer YOUR_API_KEY" \\
  ${apiBaseUrl}/inbox/${exampleLocalPart}`}
              </pre>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-zinc-800 bg-zinc-950/50 p-4 text-xs leading-relaxed text-zinc-500">
            <p className="font-medium text-zinc-400">Security & limits</p>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>API keys are stored hashed — the full key is shown only once at creation.</li>
              <li>You can only read inboxes under your assigned subdomain.</li>
              <li>Rate limit: 30 requests per minute per IP.</li>
              <li>Messages expire after 20 minutes, matching the web inbox.</li>
              <li>Never expose your API key in client-side code or public repositories.</li>
            </ul>
          </div>
        </div>

        {message && (
          <p className={`text-sm ${isError ? "text-red-400" : "text-emerald-400"}`}>{message}</p>
        )}

        <p className="text-xs text-zinc-500">
          Need Pro?{" "}
          <Link to="/dashboard?section=billing" className="text-emerald-400 hover:underline">
            Visit billing
          </Link>{" "}
          to upgrade your account.
        </p>
      </div>
    </div>
  );
}

export default UserApiAccessSection;
